import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { pdApi, type PdOpportunity, type PdSavedView } from '../api';
import { HelpPanel, type HelpContent } from '../HelpPanel';
import './OpportunityRegistry.css';
import '../screens.css';
import { SelectField, PROMPT, UNSET } from '@platform/ui-kit';

/**
 * Opportunities → Opportunity Registry.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/opportunities-list.ejs`.
 * Column order and the default sort (created date ascending, oldest first) are the
 * source's — this is a work list, and the oldest unaddressed item belongs at the top.
 *
 * THE MANDATORY FLAG has a quiet constraint worth knowing: it is stored on the budget
 * row, and `opportunity_budgets.project_id` is NOT NULL, so it cannot persist for an
 * opportunity with no project. The source silently no-ops in that case; here the toggle
 * is disabled with a reason instead of appearing to work and then forgetting.
 *
 * BULK ASSIGN only moves opportunities that are unassigned or already on the target
 * project — the server will not silently steal one from another project.
 */

const STATUS_COLOURS: Record<string, string> = {
  Draft: '#64748b',
  Approved: '#1e40af',
  'Ready for Scoring': '#92400e',
  'Scoring in Progress': '#b45309',
  Scored: '#166534',
  'In Development': '#6d28d9',
  Delivered: '#0f766e',
};


/**
 * "How this works" for this screen — the source's `opportunitiesListHelp`, verbatim.
 * Its `<strong>`/`<em>` markup and its `&mdash;`/`&rarr;` entities are the source's own.
 */
const HELP: HelpContent = {
  title: 'Opportunity Registry',
  icon: '📋',
  purpose:
    "The <strong>Opportunity Registry</strong> is the master list of every AI initiative across the portfolio. An <strong>opportunity</strong> is a <strong>single, specific AI initiative</strong> (one use case or requirement) &mdash; the discrete thing you score, cost and deliver. Each opportunity <strong>belongs to a project</strong>, the higher-level grouping that bundles related opportunities together (by business unit, geography or function); normally one project holds several opportunities. Here, each opportunity is classified against the <strong>process hierarchy</strong> (L1&rarr;L2&rarr;L3) and moves through a status lifecycle from <em>Draft</em> all the way to <em>Delivered</em>.",
  doHere: [
    "<strong>Add an opportunity</strong> manually, or <strong>bulk-import from Excel</strong> using the downloadable template.",
    "Filter and search by <strong>status</strong> or <strong>process group (L1)</strong>, and switch between <strong>Cards</strong> and <strong>Details</strong> table views.",
    "<strong>Open an opportunity</strong> to view, edit, or push it into scoring — and <strong>revert</strong> one to Draft if it needs rework.",
    "<strong>Export all data</strong> to Excel for reporting, or bulk-assign opportunities to a project.",
  ],
  fits: 'Discover &rarr; <strong>Score</strong> &rarr; Finance &rarr; Optimize &rarr; Plan &rarr; Execute',
  firstTime:
    "Click <strong>&#10133; Add Opportunity</strong>, fill in the description, project and process classification, and save. Then set its status to <em>Approved</em> and <strong>Submit to Score</strong>.",
  sections: [
    {
      label: 'Opportunity vs. Project — the boundary',
      type: 'defs',
      items: [
        { term: 'Opportunity', color: '#00897b', def: "A <strong>single, specific AI initiative</strong> (one use case or requirement) &mdash; the discrete thing you score, cost and deliver. The <em>what</em>, and what you manage on this screen." },
        { term: 'Project', color: '#1976d2', def: "The <strong>highest-level grouping</strong> an opportunity belongs to &mdash; a logical container bundling related opportunities under one owner and framework (a business unit, geography or function). The <em>where / why</em>." },
        { term: 'How they relate', color: '#5e35b1', def: "<strong>Many opportunities roll up into one project</strong>, grouped however makes logical sense. Rarely, a single complex requirement (e.g. <em>Implement Demand Planning</em>) is its own project &mdash; one project, one opportunity." },
      ],
    },
    {
      label: 'Opportunity lifecycle',
      type: 'html',
      html: "An opportunity advances left-to-right as it is evaluated and committed:<div style='margin-top:10px; font-size:12px; color:#546e7a; line-height:1.95;'>Draft &rarr; <strong style='color:#388e3c;'>Approved</strong> &rarr; Ready for Scoring &rarr; Scoring in Progress &rarr; <strong style='color:#00897b;'>Scored</strong> &rarr; Committed &rarr; In Development &rarr; <strong style='color:#00796b;'>Delivered</strong></div><div style='margin-top:8px; color:#78909c;'>An opportunity can be <strong>Submitted to Score</strong> only once it is <strong>Approved</strong>. After scoring has started, it can no longer be reverted to Draft.</div>",
    },
    {
      label: 'Statuses you will see here',
      type: 'defs',
      items: [
        { term: 'Draft', color: '#607d8b', def: 'Being captured and classified — not yet approved. Freely editable.' },
        { term: 'Approved', color: '#388e3c', def: "Approved to proceed &mdash; now eligible to be <strong>Submitted to Score</strong>. Once scoring has started it can no longer be reverted to Draft." },
        { term: 'Ready for Scoring', color: '#1976d2', def: 'Submitted to scoring and queued — waiting for an evaluator to begin scoring against its framework.' },
        { term: 'Scoring in Progress', color: '#f57c00', def: 'An evaluator is actively scoring it across the assessment dimensions.' },
        { term: 'Scored', color: '#00897b', def: 'All dimensions scored and a recommendation produced — ready to be committed to delivery (Committed → In Development → Delivered).' },
        { term: 'On Hold', color: '#9e9e9e', def: 'Paused — parked out of the active flow until it is resumed.' },
      ],
      note: "Use the status filter chips and the <strong>L1 process</strong> filter to focus the list. An <strong>Approved</strong> opportunity can be set back to <strong>Draft</strong> for rework via the status dropdown &mdash; but only until scoring has started.",
    },
  ],
  links: [
    { label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' },
  ],
};

const BASE = '/portfolio_definition/opportunities/opportunity-registry';

/**
 * The key `user_saved_views.screen_name` is stored under.
 *
 * 'opportunities-list' is the SOURCE's value, kept verbatim so a view saved in the source
 * app still resolves here. A tidier name would orphan every row already in the table.
 */
/** Where the project filter is kept between screens — this product's own key. */
const PROJECT_FILTER_KEY = 'portfolio_definition.opportunityRegistry.projects';

const SCREEN_NAME = 'opportunities-list';

/** The source's card-size slider: 240–600, step 20, default 380. */
const CARD_MIN = 240;
const CARD_MAX = 600;
const CARD_STEP = 20;

/** The card header's status pill — the source's own five colours. */
const CARD_STATUS_BG: Record<string, string> = {
  Draft: '#1976d2',
  'Ready for Scoring': '#fb8c00',
  'Scoring in Progress': '#fbc02d',
  Scored: '#388e3c',
  Approved: '#00897b',
};

/** Criticality 1–5, the source's palette. 2 takes dark text on amber. */
const CRIT_COLOUR: Record<number, string> = {
  5: '#D32F2F',
  4: '#FF9800',
  3: '#2196F3',
  2: '#FFC107',
};

export function OpportunityRegistry() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PdOpportunity[]>([]);
  const [projects, setProjects] = useState<{ project_id: string; project_description: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [assignTo, setAssignTo] = useState('');

  /*
    CARDS OR DETAILS, remembered between visits — `localStorage.opportunitiesViewType` and
    `opportunitiesCardSize`, the SOURCE's own keys and defaults (380px, 240–600 in steps of
    20). Same keys deliberately: a user who set this in the source app keeps their choice.
  */
  const [viewMode, setViewMode] = useState<'card' | 'details'>(
    () => (localStorage.getItem('opportunitiesViewType') as 'card' | 'details') || 'details',
  );
  const [cardSize, setCardSize] = useState<number>(
    () => Number(localStorage.getItem('opportunitiesCardSize')) || 380,
  );

  /*
    PROJECTS IS A MULTI-SELECT, which is why it is an array and not the single
    `projectFilter` this screen used to have. The source's own dropdown takes Ctrl+click
    for several at once and shows them comma-joined in a read-only input.
  */
  /*
    THE PROJECT FILTER SURVIVES A TRIP TO AN OPPORTUNITY AND BACK (owner-directed
    2026-09-28): "I select the project to see the opportunities within the project ...
    I hit Update the opportunity and I go back ... I see all opportunities. Instead I
    should retain the filter for the given project."

    sessionStorage, not localStorage: a filter is a working state for THIS sitting, so it
    holds while the tab is open and is gone tomorrow. The view-mode preferences above are
    localStorage because those are a lasting choice, not a filter.
  */
  const [selectedProjects, setSelectedProjects] = useState<string[]>(() => {
    try {
      const raw = sessionStorage.getItem(PROJECT_FILTER_KEY);
      const v: unknown = raw ? JSON.parse(raw) : null;
      return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
    } catch {
      return [];
    }
  });
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);

  /* All / Assigned / Unassigned, the source's three tabs. */
  const [oppTab, setOppTab] = useState<'all' | 'assigned' | 'unassigned'>('all');

  /* Saved views — the named filter sets, per screen. */
  const [savedViews, setSavedViews] = useState<PdSavedView[]>([]);
  const [currentView, setCurrentView] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const [viewIsDefault, setViewIsDefault] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pdApi.opportunities.list();
      setRows(data.opportunities);
      setProjects(data.projects);
      setSelected(new Set());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    try {
      sessionStorage.setItem(PROJECT_FILTER_KEY, JSON.stringify(selectedProjects));
    } catch {
      /* private window — the filter simply does not survive the trip */
    }
  }, [selectedProjects]);

  /*
    A REMEMBERED PROJECT THAT NO LONGER EXISTS would filter every row away and read as an
    empty registry. Once the project list is in, drop any id that is not in it.
  */
  useEffect(() => {
    if (!projects.length || !selectedProjects.length) return;
    const live = new Set(projects.map((p) => p.project_id));
    if (selectedProjects.every((id) => live.has(id))) return;
    setSelectedProjects((prev) => prev.filter((id) => live.has(id)));
  }, [projects, selectedProjects]);

  useEffect(() => {
    localStorage.setItem('opportunitiesViewType', viewMode);
  }, [viewMode]);
  useEffect(() => {
    localStorage.setItem('opportunitiesCardSize', String(cardSize));
  }, [cardSize]);

  /** "10003 - Source-to-Pay Transformation", or the bare id if the project is not loaded. */
  const projectLabel = useCallback(
    (id: string) => {
      const p = projects.find((x) => x.project_id === id);
      return p?.project_description ? `${id} - ${p.project_description}` : id;
    },
    [projects],
  );

  /* Close the project menu on any click outside it. */
  useEffect(() => {
    if (!projectMenuOpen) return;
    const close = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.pd-mdd')) setProjectMenuOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [projectMenuOpen]);

  const loadViews = useCallback(async () => {
    try {
      const d = await pdApi.savedViews.list(SCREEN_NAME);
      setSavedViews(d.views ?? []);
      /*
        A DEFAULT VIEW APPLIES ITSELF on first load, which is the whole point of marking
        one — the source reloads the page into it. Only on first load: re-applying after
        every save would throw away whatever the user had just filtered to.
      */
      const def = (d.views ?? []).find((v) => v.is_default === 1);
      if (def && !appliedDefault.current) {
        appliedDefault.current = true;
        applyView(def);
        setCurrentView(String(def.view_id));
      }
    } catch {
      /* an empty dropdown is the honest fallback */
    }
  }, []);

  useEffect(() => {
    void loadViews();
  }, [loadViews]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((o) => {
      // Assigned means it has a project — the source's own split for its three tabs.
      if (oppTab === 'assigned' && !o.project_id) return false;
      if (oppTab === 'unassigned' && o.project_id) return false;
      // An EMPTY selection means every project, not none — the source's dropdown reads
      // "Select Projects" until you pick, and shows everything until then.
      if (selectedProjects.length && !selectedProjects.includes(o.project_id ?? '')) return false;
      if (statusFilter && o.status !== statusFilter) return false;
      if (
        q &&
        !o.opportunity_id.toLowerCase().includes(q) &&
        !(o.description ?? '').toLowerCase().includes(q) &&
        !(o.owner_name ?? '').toLowerCase().includes(q)
      )
        return false;
      return true;
    });
  }, [rows, filter, statusFilter, selectedProjects, oppTab]);

  const assignedCount = useMemo(() => rows.filter((r) => r.project_id).length, [rows]);

  const statuses = useMemo(
    () => Array.from(new Set(rows.map((r) => r.status))).sort(),
    [rows],
  );

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (ok) setNotice(ok);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  /**
   * Delete, with the source's own confirmation text.
   *
   * Only reachable on a Draft — the card hides the bin for anything further along, which
   * is the source's rule and not a simplification.
   */
  const uploadRef = useRef<HTMLInputElement>(null);

  /** Bulk upload — multipart, so it does not go through the JSON client. */
  const upload = async (file: File) => {
    const fd = new FormData();
    // `excelFile` — the field name `upload.single(...)` expects on the server.
    fd.append('excelFile', file);
    setError(null);
    setNotice(null);
    try {
      const r = await fetch('/api/portfolio_definition/opportunities/upload-excel', {
        method: 'POST',
        credentials: 'same-origin',
        body: fd,
      });
      const d = (await r.json()) as { success?: boolean; error?: string; message?: string };
      if (!r.ok || d.success === false) {
        setError(d.error ?? 'Upload failed');
        return;
      }
      setNotice(d.message ?? 'Opportunities uploaded.');
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this opportunity?')) return;
    await run(() => pdApi.opportunities.remove(id), 'Opportunity deleted.');
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  /** Applied once, on first load, if a view is marked default. */
  const appliedDefault = useRef(false);

  /** Put a saved view's filters back on the screen. */
  function applyView(v: PdSavedView) {
    try {
      const cfg = JSON.parse(v.filter_config) as {
        selectedProjects?: string[];
        oppFilter?: string;
      };
      setSelectedProjects(cfg.selectedProjects ?? []);
      setFilter(cfg.oppFilter ?? '');
    } catch {
      setError(`Saved view "${v.view_name}" could not be read.`);
    }
  }

  const saveView = async () => {
    const name = viewName.trim();
    if (!name) {
      setError('Please enter a view name');
      return;
    }
    try {
      const d = await pdApi.savedViews.save({
        screen_name: SCREEN_NAME,
        view_name: name,
        // The source stringifies the config before sending; the column is text.
        filter_config: JSON.stringify({ selectedProjects, oppFilter: filter }),
        is_default: viewIsDefault ? 1 : 0,
      });
      if (!d.success) {
        setError(d.error ?? 'Failed to save view');
        return;
      }
      setSaveOpen(false);
      setViewName('');
      setViewIsDefault(false);
      setNotice('View saved successfully!');
      await loadViews();
    } catch (e) {
      setError(`Error saving view: ${(e as Error).message}`);
    }
  };

  const deleteView = async () => {
    if (!currentView) {
      setError('Select a view to delete');
      return;
    }
    const v = savedViews.find((x) => String(x.view_id) === currentView);
    if (!window.confirm(`Delete the saved view "${v?.view_name ?? ''}"?`)) return;
    try {
      await pdApi.savedViews.remove(Number(currentView));
      setCurrentView('');
      setNotice('View deleted successfully');
      await loadViews();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const money = (v: unknown) =>
    v === null || v === undefined
      ? '—'
      : Number(v).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        });

  return (
    <div className="pd-screen">
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">All Opportunities</h1>
          <p className="pd-page-header__subtitle">
            Every opportunity, any status — the master list and the entry point for scoring.
          </p>
        </div>
        {/*
          NO "New Opportunity" BUTTON AND NO COUNT HERE — the owner (2026-08-08), checked
          against the running source: adding is done by the quick-actions row's
          "Add Opportunity", and the count lives on the filter row as "N records".
          Two ways to add the same thing, and the same number twice, is what came out.
        */}
        <div className="pd-page-header__actions">
          <HelpPanel help={HELP} />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}
      {notice ? <div className="pd-notice">{notice}</div> : null}

      {/*
        QUICK ACTIONS, the source's own row: four coloured buttons on the left, the
        Cards/Details toggle and the size slider on the right. The colours are its —
        #1976d2 add, #f57c00 template, #388e3c upload, #7b1fa2 export — and they are how
        the row is scanned, so they are not reduced to one accent.
      */}
      <div className="pd-quickactions">
        <div className="pd-quickactions-left">
          <button
            type="button"
            className="pd-qa pd-qa--add"
            onClick={() => navigate(`${BASE}/new`)}
          >
            ➕ Add Opportunity
          </button>
          <a className="pd-qa pd-qa--template" href="/api/portfolio_definition/opportunities/download-template">
            📥 Download Template
          </a>
          <button
            type="button"
            className="pd-qa pd-qa--upload"
            title="Upload opportunities from Excel file"
            onClick={() => uploadRef.current?.click()}
          >
            📤 Upload Opportunities
          </button>
          <input
            ref={uploadRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void upload(f);
            }}
          />
          <a className="pd-qa pd-qa--export" href="/api/portfolio_definition/opportunities/export-all">
            📊 Export All Data
          </a>
        </div>

        <div className="pd-quickactions-right">
          <div className="pd-viewtoggle">
            <button
              type="button"
              className={viewMode === 'card' ? 'on' : ''}
              title="Card View"
              onClick={() => setViewMode('card')}
            >
              🎯 Cards
            </button>
            <button
              type="button"
              className={viewMode === 'details' ? 'on' : ''}
              title="Details View"
              onClick={() => setViewMode('details')}
            >
              📋 Details
            </button>
          </div>
          {/* The slider belongs to the card view, so it appears only with it. */}
          {viewMode === 'card' ? (
            <div className="pd-cardsize">
              <label htmlFor="opp-cardsize">Card Size:</label>
              <input
                id="opp-cardsize"
                type="range"
                min={CARD_MIN}
                max={CARD_MAX}
                step={CARD_STEP}
                value={cardSize}
                onChange={(e) => setCardSize(Number(e.target.value))}
              />
              <span>{cardSize}px</span>
            </div>
          ) : null}
        </div>
      </div>

      <div className="pd-filters">
        <input
          type="text"
          placeholder="Filter by id, description or owner…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {statuses.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        {/*
          PROJECTS — a multi-select, cloned from the source's own custom dropdown rather
          than a native <select multiple>, which cannot show "3 selected" in its closed
          state and gives no room for the Ctrl+click hint the source prints underneath.

          Plain click toggles one; Ctrl/Cmd+click adds to the selection — the source's
          behaviour and its own hint text.
        */}
        <div className="pd-mdd">
          <label className="pd-mdd-label">Projects:</label>
          <div className="pd-mdd-box">
            <input
              type="text"
              className="pd-mdd-input"
              readOnly
              /* The closed field names the project the way the menu does — "10003 -
                 Source-to-Pay Transformation", not the bare id (owner-directed
                 2026-09-28). Several selected still reads as a count: the descriptions
                 do not fit, and the menu below shows which ones are ticked. */
              value={
                selectedProjects.length === 0
                  ? 'Select Projects'
                  : selectedProjects.length === 1
                    ? projectLabel(selectedProjects[0]!)
                    : `${selectedProjects.length} projects selected`
              }
              title={selectedProjects.map(projectLabel).join(', ')}
              onClick={(e) => {
                e.stopPropagation();
                setProjectMenuOpen((o) => !o);
              }}
            />
            <span className="pd-mdd-arrow">▼</span>
            {projectMenuOpen ? (
              <div className="pd-mdd-menu">
                {projects.length === 0 ? (
                  <div className="pd-mdd-empty">No projects available</div>
                ) : (
                  projects.map((p) => {
                    const on = selectedProjects.includes(p.project_id);
                    return (
                      <div
                        key={p.project_id}
                        className={`pd-mdd-item${on ? ' selected' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          const multi = e.ctrlKey || e.metaKey;
                          setSelectedProjects((cur) => {
                            if (!multi) return on && cur.length === 1 ? [] : [p.project_id];
                            return on
                              ? cur.filter((x) => x !== p.project_id)
                              : [...cur, p.project_id];
                          });
                        }}
                      >
                        {p.project_id} - {p.project_description}
                      </div>
                    );
                  })
                )}
              </div>
            ) : null}
          </div>
          <span className="pd-mdd-hint">(Ctrl+click for multiple)</span>
        </div>

        <button
          type="button"
          className="pd-btn"
          onClick={() => {
            setFilter('');
            setStatusFilter('');
            setSelectedProjects([]);
            setCurrentView('');
          }}
        >
          Clear
        </button>

        {/* Saved views — save the current filters, load one back, delete it. */}
        <button type="button" className="pd-btn pd-btn--primary" onClick={() => setSaveOpen(true)}>
          Save View
        </button>
        <label className="pd-mdd-label">Saved Views:</label>
        <select
          value={currentView}
          onChange={(e) => {
            setCurrentView(e.target.value);
            const v = savedViews.find((x) => String(x.view_id) === e.target.value);
            if (v) applyView(v);
          }}
        >
          <option value="">-- Select View --</option>
          {savedViews.map((v) => (
            <option key={v.view_id} value={v.view_id}>
              {v.view_name}
              {v.is_default === 1 ? ' ★' : ''}
            </option>
          ))}
        </select>
        <button type="button" className="pd-btn pd-btn--danger" onClick={() => void deleteView()}>
          Delete
        </button>

        {/* The count sits here, right-aligned, as "N records" — the source's own place. */}
        <span className="pd-records">{shown.length} records</span>

        {selected.size > 0 ? (
          <>
            <span className="pd-count">{selected.size} selected →</span>
            <SelectField
              label="Project"
              width="lg"
              placeholder={PROMPT.project}
              value={assignTo}
              /* UNSET is normalised away here so `disabled={!assignTo}` still reads it as
                 "nothing chosen" — the sentinel is truthy, and would otherwise arm the
                 Assign button with a project id that does not exist. */
              onChange={(v) => setAssignTo(v === UNSET ? '' : v)}
              options={projects.map((p) => ({
                value: p.project_id,
                id: p.project_id,
                description: p.project_description,
              }))}
            />
            <button
              type="button"
              className="pd-btn pd-btn--primary"
              disabled={!assignTo}
              onClick={() =>
                run(async () => {
                  const r = await pdApi.opportunities.bulkAssign([...selected], assignTo);
                  setNotice(`${r.count} opportunity(ies) assigned to ${r.projectDescription}`);
                  setAssignTo('');
                })
              }
            >
              Assign
            </button>
          </>
        ) : null}
      </div>

      {/*
        THE THREE TABS and the view toggle, both the source's. Assigned means the
        opportunity has a project; Unassigned means it does not.
      */}
      <div className="pd-opptabs">
        {(
          [
            ['all', `All (${rows.length})`],
            ['assigned', `Assigned (${assignedCount})`],
            ['unassigned', `Unassigned (${rows.length - assignedCount})`],
          ] as const
        ).map(([key, label]) => (
          <span
            key={key}
            className={`pd-opptab${oppTab === key ? ' active' : ''}`}
            onClick={() => setOppTab(key)}
          >
            {label}
          </span>
        ))}
      </div>

      {loading ? (
        <div className="pd-loading">Loading…</div>
      ) : viewMode === 'card' ? (
        /*
          THE CARD GRID, cloned from the source's own: `repeat(auto-fill, minmax(<size>px,
          1fr))` with a 20px gap, and the card's five fixed-height bands — header 52,
          description 80, details 249, docs 40, actions 44. The heights are what keep every
          card's action row on the same line regardless of how long its description is;
          without them the grid looks ragged, which is exactly why the source pinned them.
        */
        <div
          className="pd-cardgrid"
          style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${cardSize}px, 1fr))` }}
        >
          {shown.map((o) => {
            const crit = Number(o.criticality_score) || 0;
            const score = o.overall_score == null ? null : Number(o.overall_score);
            return (
              <div className="pd-oppcard" key={o.opportunity_id}>
                <div className="pd-oppcard-head">
                  <div style={{ flex: 1 }}>
                    <div className="pd-oppcard-id">{o.opportunity_id}</div>
                    <div className="pd-oppcard-proj">📁 Project: {o.project_id || 'No Project'}</div>
                  </div>
                  <span
                    className="pd-oppcard-status"
                    style={{ background: CARD_STATUS_BG[o.status] ?? '#9e9e9e' }}
                  >
                    {o.status}
                  </span>
                </div>

                <div className="pd-oppcard-desc">{o.description}</div>

                <div className="pd-oppcard-details">
                  <div className="pd-oppcard-row">
                    <div className="pd-oppcard-lbl">Criticality</div>
                    {crit ? (
                      <span
                        className="pd-oppcard-crit"
                        style={{
                          background: CRIT_COLOUR[crit] ?? '#9E9E9E',
                          color: crit === 2 ? '#333' : '#fff',
                        }}
                      >
                        {crit} - {o.criticality_label ?? ''}
                      </span>
                    ) : (
                      <span className="pd-oppcard-dash">-</span>
                    )}
                  </div>

                  <div className="pd-oppcard-quad">
                    <div>
                      <div className="pd-oppcard-lbl">Budget</div>
                      <div className="pd-oppcard-val">{money(o.opportunity_budget)}</div>
                    </div>
                    <div>
                      <div className="pd-oppcard-lbl">Benefit</div>
                      <div className="pd-oppcard-val">{money(o.business_benefit)}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div className="pd-oppcard-lbl">Owner</div>
                      <div className="pd-oppcard-val">{o.owner_name || '-'}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="pd-oppcard-lbl">Score</div>
                      {score == null ? (
                        <span className="pd-oppcard-dash">-</span>
                      ) : (
                        <div
                          className="pd-oppcard-score"
                          style={{
                            color: score >= 4 ? '#4CAF50' : score >= 3 ? '#FF9800' : '#F44336',
                          }}
                        >
                          {score.toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="pd-oppcard-row">
                    <div className="pd-oppcard-lbl">Business Process Owner</div>
                    <div className="pd-oppcard-val">{o.business_process_owner || '-'}</div>
                  </div>
                  <div className="pd-oppcard-row">
                    <div className="pd-oppcard-lbl">Key Stakeholder</div>
                    <div className="pd-oppcard-val">{o.sponsor_keystakeholder || '-'}</div>
                  </div>

                  <div className="pd-oppcard-pair">
                    <div>
                      <div className="pd-oppcard-lbl">Created</div>
                      <div className="pd-oppcard-val">
                        {o.created_date
                          ? new Date(o.created_date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })
                          : '-'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="pd-oppcard-lbl">Mandatory</div>
                      {/* Live, as the source's is — it toggles rather than reporting. */}
                      <label className="pd-oppcard-mand">
                        <input
                          type="checkbox"
                          checked={!!o.is_mandatory}
                          disabled={!o.project_id}
                          title={
                            o.project_id
                              ? undefined
                              : 'Assign a project first — the flag is stored on the budget row.'
                          }
                          onChange={(e) =>
                            run(() =>
                              pdApi.opportunities.toggleMandatory(o.opportunity_id, e.target.checked),
                            )
                          }
                        />
                        <span style={{ color: o.is_mandatory ? '#388e3c' : '#999' }}>
                          {o.is_mandatory ? 'Yes' : 'No'}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                {/*
                  DOCS — 40px, fixed. Each of the three slots always renders: a link when
                  the opportunity has one, a greyed-out icon when it does not. The source
                  keeps the empty icon rather than collapsing the row, so the band's height
                  and every card's alignment hold regardless of how many links exist.
                */}
                <div className="pd-oppcard-docs">
                  <span className="pd-oppcard-docslbl">📄 Docs:</span>
                  <div className="pd-oppcard-docsicons">
                    {(
                      [
                        ['initial_discussion_link', 'description', '#4285f4', 'Initial Discussion'],
                        ['business_process_requirements_link', 'table_chart', '#0f9d58', 'Business Process Requirements'],
                        ['technical_requirements_link', 'code', '#f4b400', 'Technical Requirements'],
                      ] as const
                    ).map(([field, icon, colour, title]) => {
                      const href = o[field] as string | null | undefined;
                      return href ? (
                        <a key={icon} href={href} target="_blank" rel="noreferrer" title={title}>
                          <i className="material-icons" style={{ fontSize: 20, color: colour }}>
                            {icon}
                          </i>
                        </a>
                      ) : (
                        <i
                          key={icon}
                          className="material-icons"
                          style={{ fontSize: 20, color: '#ccc' }}
                          title="No document"
                        >
                          {icon}
                        </i>
                      );
                    })}
                  </div>
                </div>

                {/*
                  ACTIONS — 44px, fixed, pushed to the bottom by `margin-top: auto`.

                  WHICH ICONS APPEAR DEPENDS ON STATUS, and that is the source's rule, not a
                  simplification: 👁️ always, ✏️ only for Draft or Approved, 🗑️ only for
                  Draft. An opportunity that has gone to scoring is not editable or
                  deletable from this list.
                */}
                <div className="pd-oppcard-actions">
                  <button
                    type="button"
                    className="pd-icon-btn"
                    title="View Details"
                    onClick={() => navigate(`${BASE}/${encodeURIComponent(o.opportunity_id)}`)}
                  >
                    👁️
                  </button>
                  {o.status === 'Draft' || o.status === 'Approved' ? (
                    <button
                      type="button"
                      className="pd-icon-btn"
                      title="Edit"
                      onClick={() => navigate(`${BASE}/${encodeURIComponent(o.opportunity_id)}/edit`)}
                    >
                      ✏️
                    </button>
                  ) : null}
                  {o.status === 'Draft' ? (
                    <button
                      type="button"
                      className="pd-icon-btn"
                      title="Delete"
                      onClick={() => void remove(o.opportunity_id)}
                    >
                      🗑️
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
          {shown.length === 0 ? (
            <div className="pd-empty">No opportunities match these filters.</div>
          ) : null}
        </div>
      ) : (
        <div className="pd-table-wrap">
          <table className="pd-table">
            <thead>
              <tr>
                <th style={{ width: 34 }} />
                <th style={{ width: 170 }}>Opportunity ID</th>
                <th style={{ width: 300 }}>Description</th>
                <th style={{ width: 150 }}>Project</th>
                <th style={{ width: 140 }}>Status</th>
                <th style={{ width: 90 }}>Score</th>
                <th style={{ width: 150 }}>Owner</th>
                <th style={{ width: 140 }}>Criticality</th>
                <th style={{ width: 130 }}>Benefit</th>
                <th style={{ width: 100 }}>Mandatory</th>
                <th style={{ width: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 ? (
                <tr>
                  <td colSpan={11} className="pd-empty">
                    {rows.length === 0
                      ? 'No opportunities yet.'
                      : 'No opportunities match this filter.'}
                  </td>
                </tr>
              ) : (
                shown.map((o) => (
                  <tr key={o.opportunity_id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(o.opportunity_id)}
                        onChange={() => toggleSelect(o.opportunity_id)}
                        aria-label={`Select ${o.opportunity_id}`}
                      />
                    </td>
                    <td>{o.opportunity_id}</td>
                    <td>{o.description}</td>
                    <td>{o.project_id ?? '—'}</td>
                    <td>
                      <span
                        className="pd-badge"
                        style={{ color: STATUS_COLOURS[o.status] ?? '#64748b' }}
                      >
                        {o.status}
                      </span>
                    </td>
                    <td className="pd-num">
                      {o.overall_score === null || o.overall_score === undefined
                        ? '—'
                        : Number(o.overall_score).toFixed(2)}
                    </td>
                    <td>{o.owner_name}</td>
                    <td>{o.criticality_label ?? '—'}</td>
                    <td className="pd-num">{money(o.business_benefit)}</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={Boolean(o.is_mandatory)}
                        disabled={!o.project_id}
                        title={
                          o.project_id
                            ? 'Mandatory opportunities are always included by the optimizer'
                            : 'Assign this opportunity to a project before marking it mandatory'
                        }
                        onChange={(e) =>
                          run(() =>
                            pdApi.opportunities.toggleMandatory(
                              o.opportunity_id,
                              e.target.checked,
                            ),
                          )
                        }
                        aria-label={`Mandatory ${o.opportunity_id}`}
                      />
                    </td>
                    <td>
                      <div className="pd-row-actions">
                        <button
                          type="button"
                          className="pd-icon-btn"
                          title="View"
                          onClick={() =>
                            navigate(`${BASE}/${encodeURIComponent(o.opportunity_id)}`)
                          }
                        >
                          👁️
                        </button>
                        <button
                          type="button"
                          className="pd-icon-btn"
                          title="Edit"
                          onClick={() =>
                            navigate(`${BASE}/${encodeURIComponent(o.opportunity_id)}/edit`)
                          }
                        >
                          ✏️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Save Filter View — the source's own modal, its wording and its default tick. */}
      {saveOpen ? (
        <div className="pd-saveview-backdrop" onClick={() => setSaveOpen(false)}>
          <div className="pd-saveview" onClick={(e) => e.stopPropagation()}>
            <h3>Save Filter View</h3>
            <div className="pd-saveview-field">
              <label htmlFor="opp-viewname">View Name:</label>
              <input
                id="opp-viewname"
                type="text"
                placeholder="Enter view name..."
                value={viewName}
                onChange={(e) => setViewName(e.target.value)}
                autoFocus
              />
            </div>
            <label className="pd-saveview-default">
              <input
                type="checkbox"
                checked={viewIsDefault}
                onChange={(e) => setViewIsDefault(e.target.checked)}
              />
              <span>Set as default view for this screen</span>
            </label>
            <div className="pd-saveview-actions">
              <button type="button" className="pd-btn" onClick={() => setSaveOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="pd-btn pd-btn--primary"
                onClick={() => void saveView()}
              >
                Save View
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
